import { Box, Divider, FormControl, Skeleton, SxProps, Theme, useMediaQuery } from "@mui/material";
import { TextTab } from "components/TextTab";
import { useContext, useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import {
  selectClientLens,
  selectClientLensStatus,
  setLastViewedFeature,
  setLastViewedTab,
} from "redux/slices/clientLens";
import { selectEnableFirmFeatureSet, selectMyUserType } from "redux/slices/session";
import theme, { EllipsisSX } from "theme";
import { CaseClientStatus, CaseReviewStatus } from "Backend";
import LensReviewer from "./reviewer/Reviewer";
import TextSelectFirms from "components/TextSelectFirms";
import LoadingPattern from "components/LoadingPattern";
import RequestStatus from "redux/slices/RequestStatus";
import LensAdmin from "./admin/Admin";
import { useNavigate, useParams } from "react-router-dom";
import useSearchParamsWithUpdate from "helpers/hooks/useSearchParamsWithUpdate";
import { createPortal } from "react-dom";
import CaseAutocomplete from "components/CaseAutocomplete";
import { AppRoutes } from "AppRoutes";
import { PortalContext } from "contexts/PortalContext";
import { UserType } from "generated/graphql";
import SBRLensReviewer from "SBR/reviewer/Reviewer";
import { SBRLensAdmin } from "SBR/admin/Admin";
import { useShowSBR } from "SBR/useShowSBR";

const DefaultReviewParams = [
  {
    key: "case_status",
    value: CaseClientStatus.Active,
  },
  {
    key: "review_status",
    value: [CaseReviewStatus.New, CaseReviewStatus.NewDocuments],
  },
];

const LensHome = () => {
  const navigate = useNavigate();
  const { searchParams, updateParam, updateParams } = useSearchParamsWithUpdate();
  const showSBR = useShowSBR();

  const params = useParams<{ lensId: string }>();

  if (!params.lensId) {
    navigate("../");
    return null;
  }

  const lensId = parseInt(params.lensId);

  const navigateToCase = (caseId: number) => {
    navigate(AppRoutes.caseDetailBuilder(lensId, caseId));
  };

  const dispatch = useAppDispatch();
  const clientLens = useAppSelector(selectClientLens);
  const clientLensStatus = useAppSelector(selectClientLensStatus);
  const myUserType = useAppSelector(selectMyUserType);
  const [selectedTab, setSelectedTab] = useState<UserType | undefined>(
    (searchParams.get("tab") as UserType) || undefined
  );
  const isMobile = useMediaQuery(theme.breakpoints.only("xs"));
  const enableFirmFeatures = useAppSelector(selectEnableFirmFeatureSet);

  useEffect(() => {
    const qpTab: UserType = searchParams.get("tab") as UserType;
    if (myUserType && !qpTab) {
      updateParam("tab", myUserType, { replace: true });
      setSelectedTab(myUserType);
      if (myUserType === "reviewer") {
        updateParams(DefaultReviewParams, { replace: true });
      }
    } else if (qpTab) {
      setSelectedTab(qpTab);
    }
    dispatch(setLastViewedTab(qpTab));
  }, [myUserType, searchParams.get("tab")]);

  // On unmount, clear the lastViewedFeature from the store
  useEffect(() => {
    return () => {
      dispatch(setLastViewedFeature(undefined));
    };
  }, []);

  const { portalRefElement } = useContext(PortalContext);

  return clientLensStatus === RequestStatus.SUCCEEDED ? (
    <>
      {portalRefElement &&
        createPortal(
          <Box sx={{ display: "flex" }}>
            {myUserType === "admin" && (
              <div className="mt-2 flex items-center">
                <TextTab
                  id="lens-home-reviewer-tab"
                  active={selectedTab === "reviewer"}
                  click={() =>
                    updateParams(
                      [
                        {
                          key: "tab",
                          value: "reviewer",
                        },
                        ...DefaultReviewParams,
                      ],
                      { replace: true }
                    )
                  }
                  variant="subtitle3">
                  Reviewer
                </TextTab>

                <Divider sx={{ mx: 1, height: "16px", position: "relative", top: "4px" }} orientation="vertical" />

                <TextTab
                  id="lens-home-admin-tab"
                  active={selectedTab === "admin"}
                  click={() => updateParam("tab", "admin", { replace: true })}
                  variant="subtitle3">
                  Admin
                </TextTab>
              </div>
            )}
            {!isMobile && (
              <FormControl sx={{ width: "300px", ml: 2 }}>
                <CaseAutocomplete
                  label="Search for a case"
                  selected={undefined}
                  onChange={(selectedCase) => {
                    if (selectedCase !== undefined) {
                      navigateToCase(selectedCase.caseId);
                    }
                  }}
                  lensId={lensId}
                />
              </FormControl>
            )}
          </Box>,
          portalRefElement
        )}
      <Box
        sx={{
          minHeight: 0,
          display: "grid",
          gridAutoColumns: "1fr",
          gridTemplateColumns: "1fr 1fr",
          gridTemplateAreas: `
      "name name"
      "content content"
    `,
        }}>
        <Box
          sx={{
            gridArea: "name",
            display: "flex",
            flexDirection: "column",
            height: "100%",
            minHeight: 0,
            pt: 2,
          }}>
          {clientLens.name && (
            <p className="text-2xl font-bold flex items-center mb-2 gap-2">
              <Box sx={{ flexShrink: !isMobile ? 0 : 1, ...(EllipsisSX as SxProps<Theme>) }}>{clientLens.name}</Box>
              {enableFirmFeatures && !isMobile && (
                <>
                  <div>-</div>
                  <TextSelectFirms sx={EllipsisSX} lensId={lensId} />
                </>
              )}
            </p>
          )}
          {!clientLens.name && (
            <div>
              <Skeleton sx={{ height: "56px", width: "300px" }} />
              <Skeleton sx={{ height: "24px", width: "200px" }} />
            </div>
          )}
        </Box>
        {!!myUserType && (
          <Box
            sx={{
              gridArea: "content",
              display: "flex",
              flexDirection: "column",
              flex: 1,
              minHeight: 0,
              height: "100%",
            }}>
            {selectedTab === "reviewer" && showSBR ? (
              <SBRLensReviewer lensId={lensId} />
            ) : selectedTab === "reviewer" ? (
              <LensReviewer lensId={lensId} />
            ) : (
              <></>
            )}
            {selectedTab === "admin" && showSBR ? (
              <SBRLensAdmin lensId={lensId} />
            ) : selectedTab === "admin" ? (
              <LensAdmin lensId={lensId} />
            ) : (
              <></>
            )}
          </Box>
        )}
      </Box>
    </>
  ) : (
    <LoadingPattern centered />
  );
};

export default LensHome;
